import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { useGetDealShortlist } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import {
  getGetAllNdasWithSpecifiedLpsV2QueryKey,
  useDeleteCoinvestNdaDocumentByDocumentId,
  useGetAllNdasWithSpecifiedLpsV2,
  viewCoinvestNdaDocument,
} from "@vega-api/generated/coinvest-ndas-upload/coinvest-ndas-upload";
import { type ApiDealShortlistResponse } from "@vega-api/generated/types/apiDealShortlistResponse";
import { type DocumentMetadata } from "@vega-api/generated/types/documentMetadata";
import { type NdaResponseV2 } from "@vega-api/generated/types/ndaResponseV2";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { downloadUrlAsFile } from "@vega-core/utils/download.helpers";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

export type FlatNdaRow = Omit<NdaResponseV2, "documents"> & {
  document: DocumentMetadata;
};

type UseLpNdaTableProps = {
  dealId: string;
};

export const useLpNdaTable = ({ dealId }: UseLpNdaTableProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { data: ndaAssociationsResponse, isLoading: isLoadingNdas } =
    useGetAllNdasWithSpecifiedLpsV2(
      { dealId },
      {
        query: {
          enabled: isTruthy(dealId),
        },
      },
    );

  const flatNdaRows: FlatNdaRow[] =
    ndaAssociationsResponse?.data?.flatMap(({ documents, ...nda }) =>
      documents.map((document) => ({ ...nda, document })),
    ) ?? [];

  const { data: lpNameMapping } = useGetDealShortlist(dealId, undefined, {
    query: {
      enabled: isTruthy(dealId),
      select: ({ data }: ApiDealShortlistResponse) => {
        if (!data) {
          return {};
        }

        return data.dealShortlistItems.reduce<Record<string, string>>(
          (mapping: Record<string, string>, item) => {
            if (item.limitedPartner.id && item.limitedPartner.name) {
              mapping[item.limitedPartner.id] = item.limitedPartner.name;
            }

            return mapping;
          },
          {},
        );
      },
    },
  });

  const deleteMutation = useDeleteCoinvestNdaDocumentByDocumentId({
    mutation: {
      onSuccess: () => {
        showToast({
          variant: "success",
          title: m.status_success(),
          description: m.coInvestments_ndaRemoveSuccess(),
        });
        queryClient.invalidateQueries({
          queryKey: getGetAllNdasWithSpecifiedLpsV2QueryKey({ dealId }),
        });
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_removeNda(),
        });
      },
    },
  });

  const handleRemoveNda = (row: FlatNdaRow) => {
    if (!row.limitedPartnerId || !row.document.documentId) {
      return;
    }

    deleteMutation.mutate({
      limitedPartnerId: row.limitedPartnerId,
      documentId: row.document.documentId,
    });
  };

  const handleViewNda = async (row: FlatNdaRow) => {
    const { document } = row;

    try {
      const response = await viewCoinvestNdaDocument(document.documentId);

      const presignedUrl = response.data?.presignedUrl?.url;

      if (presignedUrl) {
        const fileName = document.nameOfDocument || "document";
        await downloadUrlAsFile(presignedUrl, fileName);
      } else throw new Error();
    } catch (error) {
      showErrorToast(error as ErrorType<unknown>, {
        defaultMessage: m.coInvestments_error_downloadNda(),
      });
    }
  };

  return {
    ndaAssociations: flatNdaRows,
    lpNameMapping,
    isLoading: isLoadingNdas,
    handleRemoveNda,
    handleViewNda,
    isDeleting: deleteMutation.isPending,
  };
};
